import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Alert,
  AlertTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  alpha,
  useTheme,
  IconButton,
  Switch,
  FormControlLabel,
  Collapse,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CalendarMonth,
  ContentCopy,
  Add,
  CheckCircle,
  Download,
  ExpandMore,
  ExpandLess,
  Person,
  Info,
  Delete,
  Visibility,
  Cancel,
  Refresh,
  Receipt,
  QrCode,
  AttachMoney,
  Percent,
  Home,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { format, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Helper to format date as YYYY-MM-DD in local timezone
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Calculate max end date based on start date (7 days max, same month)
const getMaxEndDate = (start: string): string => {
  const startDate = new Date(start + 'T12:00:00');
  const sevenDaysLater = new Date(startDate);
  sevenDaysLater.setDate(sevenDaysLater.getDate() + 6);

  // Get last day of the same month
  const lastDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

  // Return the earlier of: 7 days later, last day of month, or today
  const today = new Date();
  const candidates = [sevenDaysLater, lastDayOfMonth, today];
  const minDate = candidates.reduce((min, d) => (d < min ? d : min));

  return formatLocalDate(minDate);
};

// Calculate min start date based on end date (7 days max, same month)
const getMinStartDate = (end: string): string => {
  const endDate = new Date(end + 'T12:00:00');
  const sevenDaysEarlier = new Date(endDate);
  sevenDaysEarlier.setDate(sevenDaysEarlier.getDate() - 6);

  // Get first day of the same month
  const firstDayOfMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  // Return the later of: 7 days earlier or first day of month
  return formatLocalDate(sevenDaysEarlier > firstDayOfMonth ? sevenDaysEarlier : firstDayOfMonth);
};

interface PixKey {
  id: string;
  type: string;
  key: string;
  status: string;
}

interface InfoAdicional {
  nome: string;
  valor: string;
}

interface CobVResponse {
  txid: string;
  pixCopiaECola?: string;
  brcode?: string;
  location?: string;
  status: string;
  revisao?: number;
  calendario?: {
    criacao: string;
    dataDeVencimento: string;
    validadeAposVencimento?: number;
  };
  valor?: {
    original: string;
    multa?: { modalidade: number; valorPerc: string };
    juros?: { modalidade: number; valorPerc: string };
    abatimento?: { modalidade: number; valorPerc: string };
    desconto?: { modalidade: number; valorPerc?: string; descontoDataFixa?: Array<{ data: string; valorPerc: string }> };
  };
  chave?: string;
  devedor?: {
    nome?: string;
    cpf?: string;
    cnpj?: string;
    logradouro?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
  };
  solicitacaoPagador?: string;
  infoAdicionais?: InfoAdicional[];
}

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const MULTA_MODALIDADES = [
  { value: 1, label: 'Valor Fixo' },
  { value: 2, label: 'Percentual' },
];

const JUROS_MODALIDADES = [
  { value: 1, label: 'Valor (dias corridos)' },
  { value: 2, label: 'Percentual ao dia (dias corridos)' },
  { value: 3, label: 'Percentual ao mês (dias corridos)' },
  { value: 4, label: 'Percentual ao ano (dias corridos)' },
  { value: 5, label: 'Valor (dias úteis)' },
  { value: 6, label: 'Percentual ao dia (dias úteis)' },
  { value: 7, label: 'Percentual ao mês (dias úteis)' },
  { value: 8, label: 'Percentual ao ano (dias úteis)' },
];

const DESCONTO_MODALIDADES = [
  { value: 1, label: 'Valor Fixo até data(s) específica(s)' },
  { value: 2, label: 'Percentual até data(s) específica(s)' },
  { value: 3, label: 'Valor por antecipação (dia corrido)' },
  { value: 4, label: 'Valor por antecipação (dia útil)' },
  { value: 5, label: 'Percentual por antecipação (dia corrido)' },
  { value: 6, label: 'Percentual por antecipação (dia útil)' },
];

const PixCobVPage: React.FC = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [keys, setKeys] = useState<PixKey[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [cobvList, setCobvList] = useState<CobVResponse[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // List filters - default to last 7 days (Sicoob API limit)
  const today = formatLocalDate(new Date());
  const sixDaysAgo = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return formatLocalDate(d);
  })();

  const [filterStartDate, setFilterStartDate] = useState(sixDaysAgo);
  const [filterEndDate, setFilterEndDate] = useState(today);

  // Handle start date change - auto-adjust end date if needed
  const handleFilterStartChange = (newStart: string) => {
    setFilterStartDate(newStart);
    const maxEnd = getMaxEndDate(newStart);
    if (filterEndDate > maxEnd) {
      setFilterEndDate(maxEnd);
    }
    const startMonth = new Date(newStart + 'T12:00:00').getMonth();
    const endMonth = new Date(filterEndDate + 'T12:00:00').getMonth();
    if (startMonth !== endMonth) {
      setFilterEndDate(maxEnd);
    }
  };

  // Handle end date change - auto-adjust start date if needed
  const handleFilterEndChange = (newEnd: string) => {
    setFilterEndDate(newEnd);
    const minStart = getMinStartDate(newEnd);
    if (filterStartDate < minStart) {
      setFilterStartDate(minStart);
    }
    const startMonth = new Date(filterStartDate + 'T12:00:00').getMonth();
    const endMonth = new Date(newEnd + 'T12:00:00').getMonth();
    if (startMonth !== endMonth) {
      setFilterStartDate(minStart);
    }
  };

  // Charge form
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [dataVencimento, setDataVencimento] = useState<Date | null>(addDays(new Date(), 7));
  const [validadeAposVencimento, setValidadeAposVencimento] = useState('30');

  // Devedor (required for CobV)
  const [devedorNome, setDevedorNome] = useState('');
  const [devedorDocType, setDevedorDocType] = useState<'cpf' | 'cnpj'>('cpf');
  const [devedorDoc, setDevedorDoc] = useState('');
  const [showEndereco, setShowEndereco] = useState(false);
  const [devedorLogradouro, setDevedorLogradouro] = useState('');
  const [devedorCidade, setDevedorCidade] = useState('');
  const [devedorUf, setDevedorUf] = useState('');
  const [devedorCep, setDevedorCep] = useState('');

  // Multa (optional)
  const [enableMulta, setEnableMulta] = useState(false);
  const [multaModalidade, setMultaModalidade] = useState(2);
  const [multaValor, setMultaValor] = useState('');

  // Juros (optional)
  const [enableJuros, setEnableJuros] = useState(false);
  const [jurosModalidade, setJurosModalidade] = useState(2);
  const [jurosValor, setJurosValor] = useState('');

  // Desconto (optional)
  const [enableDesconto, setEnableDesconto] = useState(false);
  const [descontoModalidade, setDescontoModalidade] = useState(2);
  const [descontoValor, setDescontoValor] = useState('');
  const [descontoDataFixa, setDescontoDataFixa] = useState<Array<{ data: Date | null; valorPerc: string }>>([]);

  // Info Adicionais (optional)
  const [showInfoAdicionais, setShowInfoAdicionais] = useState(false);
  const [infoAdicionais, setInfoAdicionais] = useState<InfoAdicional[]>([]);
  const [newInfoNome, setNewInfoNome] = useState('');
  const [newInfoValor, setNewInfoValor] = useState('');

  // Result
  const [generatedCharge, setGeneratedCharge] = useState<CobVResponse | null>(null);
  const [copied, setCopied] = useState(false);

  // Details modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCobV, setSelectedCobV] = useState<CobVResponse | null>(null);

  useEffect(() => {
    loadKeys();
  }, []);

  // Only reload CobV list when switching tabs if user has already searched
  useEffect(() => {
    if (tab === 1 && hasSearched) {
      loadCobVList();
    }
  }, [tab]);

  const loadKeys = async () => {
    setLoadingKeys(true);
    try {
      const response = await api.get('/pix/keys');
      if (response?.success) {
        setKeys(response.data || []);
        if (response.data?.length > 0) {
          setSelectedKey(response.data[0].key);
        }
      }
    } catch (err) {
      console.error('Error loading keys:', err);
    } finally {
      setLoadingKeys(false);
    }
  };

  const loadCobVList = async () => {
    setLoadingList(true);
    setListError(null);
    setHasSearched(true);

    try {
      // Use local dates with proper timezone handling
      const startISO = new Date(filterStartDate + 'T00:00:00').toISOString();
      const endISO = new Date(filterEndDate + 'T23:59:59').toISOString();

      const response = await api.get('/pix/cobv', {
        params: {
          inicio: startISO,
          fim: endISO,
        },
      });

      if (response?.success) {
        setCobvList(response.data || []);
      } else {
        setListError(response?.error?.message || 'Erro ao carregar cobranças');
      }
    } catch (err: any) {
      console.error('Error loading CobV list:', err);
      setListError(err.message || 'Erro ao carregar cobranças');
    } finally {
      setLoadingList(false);
    }
  };

  const handleAddInfoAdicional = () => {
    if (newInfoNome.trim() && newInfoValor.trim()) {
      if (infoAdicionais.length >= 50) {
        toast.error('Máximo de 50 informações adicionais');
        return;
      }
      setInfoAdicionais([...infoAdicionais, { nome: newInfoNome.trim(), valor: newInfoValor.trim() }]);
      setNewInfoNome('');
      setNewInfoValor('');
    }
  };

  const handleRemoveInfoAdicional = (index: number) => {
    setInfoAdicionais(infoAdicionais.filter((_, i) => i !== index));
  };

  const handleAddDescontoDataFixa = () => {
    if (descontoDataFixa.length >= 3) {
      toast.error('Máximo de 3 datas de desconto');
      return;
    }
    setDescontoDataFixa([...descontoDataFixa, { data: null, valorPerc: '' }]);
  };

  const handleRemoveDescontoDataFixa = (index: number) => {
    setDescontoDataFixa(descontoDataFixa.filter((_, i) => i !== index));
  };

  const handleCreateCobV = async () => {
    if (!selectedKey) {
      toast.error('Selecione uma chave PIX');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    if (!dataVencimento) {
      toast.error('Informe a data de vencimento');
      return;
    }

    if (!devedorNome || !devedorDoc) {
      toast.error('Nome e CPF/CNPJ do devedor são obrigatórios para CobV');
      return;
    }

    setLoading(true);
    setGeneratedCharge(null);

    try {
      const cobVData: any = {
        calendario: {
          dataDeVencimento: format(dataVencimento, 'yyyy-MM-dd'),
          validadeAposVencimento: parseInt(validadeAposVencimento) || 30,
        },
        valor: {
          original: parseFloat(amount).toFixed(2),
        },
        chave: selectedKey,
        solicitacaoPagador: description || 'Cobrança PIX com Vencimento',
        devedor: {
          nome: devedorNome,
        },
      };

      // Devedor document
      if (devedorDocType === 'cpf') {
        cobVData.devedor.cpf = devedorDoc.replace(/\D/g, '');
      } else {
        cobVData.devedor.cnpj = devedorDoc.replace(/\D/g, '');
      }

      // Devedor address (optional)
      if (showEndereco) {
        if (devedorLogradouro) cobVData.devedor.logradouro = devedorLogradouro;
        if (devedorCidade) cobVData.devedor.cidade = devedorCidade;
        if (devedorUf) cobVData.devedor.uf = devedorUf;
        if (devedorCep) cobVData.devedor.cep = devedorCep.replace(/\D/g, '');
      }

      // Multa
      if (enableMulta && multaValor) {
        cobVData.valor.multa = {
          modalidade: multaModalidade,
          valorPerc: parseFloat(multaValor).toFixed(2),
        };
      }

      // Juros
      if (enableJuros && jurosValor) {
        cobVData.valor.juros = {
          modalidade: jurosModalidade,
          valorPerc: parseFloat(jurosValor).toFixed(2),
        };
      }

      // Desconto
      if (enableDesconto) {
        cobVData.valor.desconto = {
          modalidade: descontoModalidade,
        };

        // Modes 1 and 2 use descontoDataFixa
        if ([1, 2].includes(descontoModalidade) && descontoDataFixa.length > 0) {
          cobVData.valor.desconto.descontoDataFixa = descontoDataFixa
            .filter(d => d.data && d.valorPerc)
            .map(d => ({
              data: format(d.data!, 'yyyy-MM-dd'),
              valorPerc: parseFloat(d.valorPerc).toFixed(2),
            }));
        } else if (descontoValor) {
          // Other modes use valorPerc directly
          cobVData.valor.desconto.valorPerc = parseFloat(descontoValor).toFixed(2);
        }
      }

      // Informações adicionais
      if (infoAdicionais.length > 0) {
        cobVData.infoAdicionais = infoAdicionais;
      }

      const response = await api.post('/pix/cobv', cobVData);

      if (response?.success) {
        setGeneratedCharge(response.data);
        toast.success('Cobrança com vencimento criada com sucesso!');
      } else {
        toast.error(response?.error?.message || 'Erro ao gerar cobrança');
      }
    } catch (err: any) {
      console.error('Error creating CobV:', err);
      toast.error(err.message || 'Erro ao criar cobrança com vencimento');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (txid: string) => {
    try {
      const response = await api.get(`/pix/cobv/${txid}`);
      if (response?.success) {
        setSelectedCobV(response.data);
        setDetailsOpen(true);
      }
    } catch (err) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleCancelCobV = async (txid: string) => {
    if (!window.confirm('Deseja cancelar esta cobrança?')) return;

    try {
      const response = await api.delete(`/pix/cobv/${txid}`);
      if (response?.success) {
        toast.success('Cobrança cancelada');
        loadCobVList();
        setDetailsOpen(false);
      } else {
        toast.error(response?.error?.message || 'Erro ao cancelar');
      }
    } catch (err) {
      toast.error('Erro ao cancelar cobrança');
    }
  };

  const handleCopyPixCode = () => {
    const code = generatedCharge?.pixCopiaECola || generatedCharge?.brcode || selectedCobV?.pixCopiaECola || selectedCobV?.brcode;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleDownloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      downloadLink.download = `cobv-${generatedCharge?.txid || selectedCobV?.txid}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleNewCharge = () => {
    setGeneratedCharge(null);
    setAmount('');
    setDescription('');
    setDataVencimento(addDays(new Date(), 7));
    setDevedorNome('');
    setDevedorDoc('');
    setDevedorLogradouro('');
    setDevedorCidade('');
    setDevedorUf('');
    setDevedorCep('');
    setEnableMulta(false);
    setMultaValor('');
    setEnableJuros(false);
    setJurosValor('');
    setEnableDesconto(false);
    setDescontoValor('');
    setDescontoDataFixa([]);
    setInfoAdicionais([]);
    setShowInfoAdicionais(false);
    setShowEndereco(false);
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ATIVA': return 'info';
      case 'CONCLUIDA': return 'success';
      case 'REMOVIDA_PELO_USUARIO_RECEBEDOR': return 'error';
      case 'REMOVIDA_PELO_PSP': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ATIVA': return 'Ativa';
      case 'CONCLUIDA': return 'Concluída';
      case 'REMOVIDA_PELO_USUARIO_RECEBEDOR': return 'Cancelada';
      case 'REMOVIDA_PELO_PSP': return 'Removida';
      default: return status;
    }
  };

  const pixCode = generatedCharge?.pixCopiaECola || generatedCharge?.brcode;
  const modalPixCode = selectedCobV?.pixCopiaECola || selectedCobV?.brcode;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        <CalendarMonth sx={{ mr: 1, verticalAlign: 'middle' }} />
        Cobrança com Vencimento (CobV)
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Nova Cobrança" />
          <Tab label="Consultar Cobranças" />
        </Tabs>

        {tab === 0 && (
          <>
            {generatedCharge ? (
              <Box>
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
                  <AlertTitle>Cobrança criada com sucesso!</AlertTitle>
                  TXID: {generatedCharge.txid}
                </Alert>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: '#fff',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 1, mb: 2 }}>
                        {pixCode && (
                          <QRCodeSVG id="qr-code-svg" value={pixCode} size={220} level="M" includeMargin />
                        )}
                      </Box>
                      <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                        {formatCurrency(generatedCharge.valor?.original || amount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Vencimento: {formatDate(generatedCharge.calendario?.dataDeVencimento || '')}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ width: '100%', mb: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                          onClick={handleCopyPixCode}
                          color={copied ? 'success' : 'primary'}
                          fullWidth
                        >
                          {copied ? 'Copiado!' : 'Copiar Código'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={handleDownloadQRCode}
                          fullWidth
                        >
                          Baixar QR
                        </Button>
                      </Stack>

                      <Divider sx={{ width: '100%', my: 1 }} />

                      <Typography variant="caption" color="text.secondary">
                        Código Copia e Cola
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          mt: 1,
                          width: '100%',
                          maxHeight: 80,
                          overflow: 'auto',
                          backgroundColor: alpha(theme.palette.grey[500], 0.08),
                        }}
                      >
                        <Typography variant="caption" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                          {pixCode}
                        </Typography>
                      </Paper>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        Detalhes da Cobrança
                      </Typography>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">TXID</Typography>
                          <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                            {generatedCharge.txid}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip label={getStatusLabel(generatedCharge.status)} color={getStatusColor(generatedCharge.status) as any} size="small" />
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Valor</Typography>
                          <Typography variant="body2">{formatCurrency(generatedCharge.valor?.original || amount)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Vencimento</Typography>
                          <Typography variant="body2">{formatDate(generatedCharge.calendario?.dataDeVencimento || '')}</Typography>
                        </Box>
                        {generatedCharge.calendario?.validadeAposVencimento && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Validade após vencimento</Typography>
                            <Typography variant="body2">{generatedCharge.calendario.validadeAposVencimento} dias</Typography>
                          </Box>
                        )}
                        {generatedCharge.devedor && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Devedor</Typography>
                            <Typography variant="body2">
                              {generatedCharge.devedor.nome}
                              {generatedCharge.devedor.cpf && ` (CPF: ***${generatedCharge.devedor.cpf.slice(-4)})`}
                              {generatedCharge.devedor.cnpj && ` (CNPJ: ***${generatedCharge.devedor.cnpj.slice(-4)})`}
                            </Typography>
                          </Box>
                        )}
                        {generatedCharge.valor?.multa && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Multa</Typography>
                            <Typography variant="body2">
                              {generatedCharge.valor.multa.modalidade === 1 ? 'R$ ' : ''}
                              {generatedCharge.valor.multa.valorPerc}
                              {generatedCharge.valor.multa.modalidade === 2 ? '%' : ''}
                            </Typography>
                          </Box>
                        )}
                        {generatedCharge.valor?.juros && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Juros</Typography>
                            <Typography variant="body2">
                              {generatedCharge.valor.juros.valorPerc}
                              {[2, 3, 4, 6, 7, 8].includes(generatedCharge.valor.juros.modalidade) ? '%' : ' (valor)'}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      <Button variant="outlined" startIcon={<Add />} onClick={handleNewCharge} fullWidth>
                        Nova Cobrança
                      </Button>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <Stack spacing={2}>
                    {/* Chave PIX */}
                    <FormControl fullWidth>
                      <InputLabel>Chave PIX para Recebimento *</InputLabel>
                      <Select
                        value={selectedKey}
                        label="Chave PIX para Recebimento *"
                        onChange={(e) => setSelectedKey(e.target.value)}
                        disabled={loadingKeys}
                      >
                        {keys.map((key) => (
                          <MenuItem key={key.id} value={key.key}>
                            {key.type}: {key.key}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {keys.length === 0 && !loadingKeys && (
                      <Alert severity="warning">
                        Você precisa cadastrar uma chave PIX primeiro.
                      </Alert>
                    )}

                    <Divider />

                    {/* Valor e Vencimento */}
                    <Typography variant="subtitle2" color="text.secondary">
                      <AttachMoney sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      Valor e Vencimento
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Valor *"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                          }}
                          placeholder="0,00"
                          type="number"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DatePicker
                          label="Data de Vencimento *"
                          value={dataVencimento}
                          onChange={(newValue) => setDataVencimento(newValue)}
                          minDate={new Date()}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </Grid>
                    </Grid>

                    <TextField
                      fullWidth
                      label="Validade após vencimento (dias)"
                      value={validadeAposVencimento}
                      onChange={(e) => setValidadeAposVencimento(e.target.value)}
                      type="number"
                      helperText="Quantidade de dias após o vencimento que a cobrança pode ser paga"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">dias</InputAdornment>,
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Descrição (exibida ao pagador)"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={2}
                      placeholder="Ex: Pagamento ref. NF 12345"
                    />

                    <Divider />

                    {/* Devedor (obrigatório) */}
                    <Typography variant="subtitle2" color="text.secondary">
                      <Person sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      Dados do Devedor (obrigatório)
                    </Typography>

                    <TextField
                      fullWidth
                      label="Nome do Devedor *"
                      value={devedorNome}
                      onChange={(e) => setDevedorNome(e.target.value)}
                    />

                    <Stack direction="row" spacing={1}>
                      <FormControl sx={{ minWidth: 100 }}>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={devedorDocType}
                          label="Tipo"
                          onChange={(e) => setDevedorDocType(e.target.value as 'cpf' | 'cnpj')}
                        >
                          <MenuItem value="cpf">CPF</MenuItem>
                          <MenuItem value="cnpj">CNPJ</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label={devedorDocType === 'cpf' ? 'CPF *' : 'CNPJ *'}
                        value={devedorDoc}
                        onChange={(e) => setDevedorDoc(e.target.value.replace(/\D/g, ''))}
                        placeholder={devedorDocType === 'cpf' ? '00000000000' : '00000000000000'}
                        inputProps={{ maxLength: devedorDocType === 'cpf' ? 11 : 14 }}
                      />
                    </Stack>

                    {/* Endereço (opcional) */}
                    <Box>
                      <Button
                        variant="text"
                        startIcon={<Home />}
                        endIcon={showEndereco ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setShowEndereco(!showEndereco)}
                      >
                        Endereço (opcional)
                      </Button>
                      <Collapse in={showEndereco}>
                        <Stack spacing={2} sx={{ mt: 2, pl: 2 }}>
                          <TextField
                            fullWidth
                            label="Logradouro"
                            value={devedorLogradouro}
                            onChange={(e) => setDevedorLogradouro(e.target.value)}
                            size="small"
                          />
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                label="Cidade"
                                value={devedorCidade}
                                onChange={(e) => setDevedorCidade(e.target.value)}
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>UF</InputLabel>
                                <Select value={devedorUf} label="UF" onChange={(e) => setDevedorUf(e.target.value)}>
                                  {ESTADOS_BR.map((uf) => (
                                    <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                fullWidth
                                label="CEP"
                                value={devedorCep}
                                onChange={(e) => setDevedorCep(e.target.value.replace(/\D/g, ''))}
                                size="small"
                                inputProps={{ maxLength: 8 }}
                              />
                            </Grid>
                          </Grid>
                        </Stack>
                      </Collapse>
                    </Box>

                    <Divider />

                    {/* Multa, Juros e Desconto */}
                    <Typography variant="subtitle2" color="text.secondary">
                      <Percent sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      Multa, Juros e Desconto (opcional)
                    </Typography>

                    {/* Multa */}
                    <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                      <FormControlLabel
                        control={<Switch checked={enableMulta} onChange={(e) => setEnableMulta(e.target.checked)} />}
                        label="Aplicar multa após vencimento"
                      />
                      <Collapse in={enableMulta}>
                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                          <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Tipo</InputLabel>
                            <Select
                              value={multaModalidade}
                              label="Tipo"
                              onChange={(e) => setMultaModalidade(e.target.value as number)}
                              size="small"
                            >
                              {MULTA_MODALIDADES.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            fullWidth
                            label={multaModalidade === 1 ? 'Valor (R$)' : 'Percentual (%)'}
                            value={multaValor}
                            onChange={(e) => setMultaValor(e.target.value)}
                            type="number"
                            size="small"
                            InputProps={{
                              startAdornment: multaModalidade === 1 ? <InputAdornment position="start">R$</InputAdornment> : null,
                              endAdornment: multaModalidade === 2 ? <InputAdornment position="end">%</InputAdornment> : null,
                            }}
                          />
                        </Stack>
                      </Collapse>
                    </Box>

                    {/* Juros */}
                    <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                      <FormControlLabel
                        control={<Switch checked={enableJuros} onChange={(e) => setEnableJuros(e.target.checked)} />}
                        label="Aplicar juros após vencimento"
                      />
                      <Collapse in={enableJuros}>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Modalidade de Juros</InputLabel>
                            <Select
                              value={jurosModalidade}
                              label="Modalidade de Juros"
                              onChange={(e) => setJurosModalidade(e.target.value as number)}
                            >
                              {JUROS_MODALIDADES.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            fullWidth
                            label={[1, 5].includes(jurosModalidade) ? 'Valor (R$)' : 'Percentual (%)'}
                            value={jurosValor}
                            onChange={(e) => setJurosValor(e.target.value)}
                            type="number"
                            size="small"
                            InputProps={{
                              startAdornment: [1, 5].includes(jurosModalidade) ? <InputAdornment position="start">R$</InputAdornment> : null,
                              endAdornment: ![1, 5].includes(jurosModalidade) ? <InputAdornment position="end">%</InputAdornment> : null,
                            }}
                          />
                        </Stack>
                      </Collapse>
                    </Box>

                    {/* Desconto */}
                    <Box sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                      <FormControlLabel
                        control={<Switch checked={enableDesconto} onChange={(e) => setEnableDesconto(e.target.checked)} />}
                        label="Aplicar desconto para pagamento antecipado"
                      />
                      <Collapse in={enableDesconto}>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Modalidade de Desconto</InputLabel>
                            <Select
                              value={descontoModalidade}
                              label="Modalidade de Desconto"
                              onChange={(e) => setDescontoModalidade(e.target.value as number)}
                            >
                              {DESCONTO_MODALIDADES.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {[1, 2].includes(descontoModalidade) ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Descontos por data (até 3)
                              </Typography>
                              {descontoDataFixa.map((item, idx) => (
                                <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                                  <DatePicker
                                    label="Data"
                                    value={item.data}
                                    onChange={(newValue) => {
                                      const updated = [...descontoDataFixa];
                                      updated[idx].data = newValue;
                                      setDescontoDataFixa(updated);
                                    }}
                                    maxDate={dataVencimento || undefined}
                                    slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                                  />
                                  <TextField
                                    label={descontoModalidade === 1 ? 'Valor' : '%'}
                                    value={item.valorPerc}
                                    onChange={(e) => {
                                      const updated = [...descontoDataFixa];
                                      updated[idx].valorPerc = e.target.value;
                                      setDescontoDataFixa(updated);
                                    }}
                                    type="number"
                                    size="small"
                                    sx={{ width: 120 }}
                                    InputProps={{
                                      startAdornment: descontoModalidade === 1 ? <InputAdornment position="start">R$</InputAdornment> : null,
                                      endAdornment: descontoModalidade === 2 ? <InputAdornment position="end">%</InputAdornment> : null,
                                    }}
                                  />
                                  <IconButton size="small" onClick={() => handleRemoveDescontoDataFixa(idx)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Stack>
                              ))}
                              <Button size="small" startIcon={<Add />} onClick={handleAddDescontoDataFixa}>
                                Adicionar Data
                              </Button>
                            </Box>
                          ) : (
                            <TextField
                              fullWidth
                              label={[3, 4].includes(descontoModalidade) ? 'Valor por dia (R$)' : 'Percentual por dia (%)'}
                              value={descontoValor}
                              onChange={(e) => setDescontoValor(e.target.value)}
                              type="number"
                              size="small"
                              InputProps={{
                                startAdornment: [3, 4].includes(descontoModalidade) ? <InputAdornment position="start">R$</InputAdornment> : null,
                                endAdornment: [5, 6].includes(descontoModalidade) ? <InputAdornment position="end">%</InputAdornment> : null,
                              }}
                            />
                          )}
                        </Stack>
                      </Collapse>
                    </Box>

                    <Divider />

                    {/* Informações Adicionais (opcional) */}
                    <Box>
                      <Button
                        variant="text"
                        startIcon={<Info />}
                        endIcon={showInfoAdicionais ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setShowInfoAdicionais(!showInfoAdicionais)}
                      >
                        Informações Adicionais (opcional)
                      </Button>
                      <Collapse in={showInfoAdicionais}>
                        <Stack spacing={2} sx={{ mt: 2, pl: 2 }}>
                          {infoAdicionais.map((info, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={`${info.nome}: ${info.valor}`} onDelete={() => handleRemoveInfoAdicional(idx)} size="small" />
                            </Box>
                          ))}
                          <Stack direction="row" spacing={1} alignItems="flex-end">
                            <TextField
                              label="Nome"
                              value={newInfoNome}
                              onChange={(e) => setNewInfoNome(e.target.value)}
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ maxLength: 50 }}
                            />
                            <TextField
                              label="Valor"
                              value={newInfoValor}
                              onChange={(e) => setNewInfoValor(e.target.value)}
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ maxLength: 200 }}
                            />
                            <IconButton color="primary" onClick={handleAddInfoAdicional} disabled={!newInfoNome.trim() || !newInfoValor.trim()}>
                              <Add />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Collapse>
                    </Box>

                    <Divider />

                    {/* Submit */}
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CalendarMonth />}
                      onClick={handleCreateCobV}
                      disabled={loading || !selectedKey || !amount || !dataVencimento || !devedorNome || !devedorDoc}
                    >
                      {loading ? 'Criando...' : 'Criar Cobrança com Vencimento'}
                    </Button>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Paper
                    sx={{
                      p: 3,
                      backgroundColor: alpha(theme.palette.grey[500], 0.05),
                      position: 'sticky',
                      top: 20,
                    }}
                  >
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <AlertTitle>CobV - Cobrança com Vencimento</AlertTitle>
                      <Typography variant="body2">
                        Diferente da cobrança imediata, a CobV permite definir:
                      </Typography>
                      <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                        <li>Data de vencimento</li>
                        <li>Multa e juros por atraso</li>
                        <li>Descontos por pagamento antecipado</li>
                        <li>Dados completos do devedor</li>
                      </ul>
                    </Alert>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Resumo</Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Valor:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {amount ? formatCurrency(amount) : 'R$ 0,00'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Vencimento:</Typography>
                        <Typography variant="body2">
                          {dataVencimento ? format(dataVencimento, 'dd/MM/yyyy', { locale: ptBR }) : '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Devedor:</Typography>
                        <Typography variant="body2">{devedorNome || '-'}</Typography>
                      </Box>
                      {enableMulta && multaValor && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Multa:</Typography>
                          <Typography variant="body2">
                            {multaModalidade === 1 ? `R$ ${multaValor}` : `${multaValor}%`}
                          </Typography>
                        </Box>
                      )}
                      {enableJuros && jurosValor && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Juros:</Typography>
                          <Typography variant="body2">
                            {[1, 5].includes(jurosModalidade) ? `R$ ${jurosValor}` : `${jurosValor}%`}
                          </Typography>
                        </Box>
                      )}
                      {enableDesconto && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Desconto:</Typography>
                          <Typography variant="body2" color="success.main">Ativado</Typography>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </>
        )}

        {tab === 1 && (
          <Box>
            {/* Date Filters */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Data Início"
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => handleFilterStartChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: filterEndDate,
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Data Fim"
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => handleFilterEndChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: filterStartDate,
                      max: today,
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={loadingList ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                    onClick={loadCobVList}
                    disabled={loadingList}
                  >
                    Consultar
                  </Button>
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Máximo de 7 dias, dentro do mesmo mês (limitação da API Sicoob)
              </Typography>
            </Paper>

            {listError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListError(null)}>
                {listError}
              </Alert>
            )}

            {loadingList ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : cobvList.length === 0 && !listError ? (
              <Alert severity="info">
                {hasSearched
                  ? 'Nenhuma cobrança com vencimento encontrada no período selecionado.'
                  : 'Selecione o período e clique em "Consultar" para buscar as cobranças.'}
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>TXID</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell>Vencimento</TableCell>
                      <TableCell>Devedor</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cobvList.map((cobv) => (
                      <TableRow key={cobv.txid}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                            {cobv.txid.substring(0, 16)}...
                          </Typography>
                        </TableCell>
                        <TableCell>{formatCurrency(cobv.valor?.original || '0')}</TableCell>
                        <TableCell>{formatDate(cobv.calendario?.dataDeVencimento || '')}</TableCell>
                        <TableCell>{cobv.devedor?.nome || '-'}</TableCell>
                        <TableCell>
                          <Chip label={getStatusLabel(cobv.status)} color={getStatusColor(cobv.status) as any} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleViewDetails(cobv.txid)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                          {cobv.status === 'ATIVA' && (
                            <IconButton size="small" color="error" onClick={() => handleCancelCobV(cobv.txid)}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt />
            Detalhes da Cobrança
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCobV && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {modalPixCode && (
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <QRCodeSVG id="qr-code-svg" value={modalPixCode} size={180} level="M" includeMargin />
                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                      <Button size="small" startIcon={<ContentCopy />} onClick={handleCopyPixCode}>
                        Copiar
                      </Button>
                      <Button size="small" startIcon={<Download />} onClick={handleDownloadQRCode}>
                        Baixar
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">TXID</Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                      {selectedCobV.txid}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip label={getStatusLabel(selectedCobV.status)} color={getStatusColor(selectedCobV.status) as any} size="small" />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Valor</Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(selectedCobV.valor?.original || '0')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Vencimento</Typography>
                    <Typography variant="body2">
                      {formatDate(selectedCobV.calendario?.dataDeVencimento || '')}
                    </Typography>
                  </Box>
                  {selectedCobV.devedor && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Devedor</Typography>
                      <Typography variant="body2">{selectedCobV.devedor.nome}</Typography>
                      {selectedCobV.devedor.logradouro && (
                        <Typography variant="caption" color="text.secondary">
                          {selectedCobV.devedor.logradouro}, {selectedCobV.devedor.cidade} - {selectedCobV.devedor.uf}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {selectedCobV.valor?.multa && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Multa</Typography>
                      <Typography variant="body2">
                        {selectedCobV.valor.multa.modalidade === 1 ? 'R$ ' : ''}
                        {selectedCobV.valor.multa.valorPerc}
                        {selectedCobV.valor.multa.modalidade === 2 ? '%' : ''}
                      </Typography>
                    </Box>
                  )}
                  {selectedCobV.valor?.juros && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Juros</Typography>
                      <Typography variant="body2">{selectedCobV.valor.juros.valorPerc}</Typography>
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedCobV?.status === 'ATIVA' && (
            <Button color="error" onClick={() => handleCancelCobV(selectedCobV.txid)}>
              Cancelar Cobrança
            </Button>
          )}
          <Button onClick={() => setDetailsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PixCobVPage;
